import React, { useState, useEffect } from 'react';
import { 
  Form, 
  InputNumber, 
  Select, 
  Switch, 
  Divider, 
  Row, 
  Col, 
  Card, 
  Typography, 
  Space,
  Tag,
  Alert,
  Slider
} from 'antd';

const { Title, Text } = Typography;
const { Option } = Select;

interface ScreeningCriteriaFormProps {
  form: any;
  initialValues?: any;
  onChange?: (values: any) => void;
}

const ScreeningCriteriaForm: React.FC<ScreeningCriteriaFormProps> = ({
  form,
  initialValues,
  onChange
}) => {
  const [weights, setWeights] = useState({
    experienceWeight: 25,
    skillsWeight: 45,
    educationWeight: 15,
    locationWeight: 15
  });

  const [totalWeight, setTotalWeight] = useState(100);

  useEffect(() => {
    if (initialValues) {
      const newWeights = {
        experienceWeight: initialValues.experienceWeight || 25,
        skillsWeight: initialValues.skillsWeight || 45,
        educationWeight: initialValues.educationWeight || 15,
        locationWeight: initialValues.locationWeight || 15
      };
      setWeights(newWeights);
      setTotalWeight(Object.values(newWeights).reduce((a, b) => a + b, 0));
    }
  }, [initialValues]);

  const handleWeightChange = (field: string, value: number) => {
    const newWeights = { ...weights, [field]: value };
    setWeights(newWeights);
    
    const total = Object.values(newWeights).reduce((a, b) => a + b, 0);
    setTotalWeight(total);
    
    // Update form values
    form.setFieldsValue(newWeights);
    
    if (onChange) {
      onChange({ ...form.getFieldsValue(), ...newWeights });
    }
  };

  const skillSuggestions = [
    'JavaScript', 'Python', 'Java', 'React', 'Node.js', 'Angular', 'Vue.js',
    'TypeScript', 'C++', 'C#', 'PHP', 'Ruby', 'Go', 'Rust', 'Swift',
    'AWS', 'Azure', 'Docker', 'Kubernetes', 'MongoDB', 'PostgreSQL', 'MySQL',
    'Git', 'Jenkins', 'Agile', 'Scrum', 'REST API', 'GraphQL', 'Machine Learning'
  ];

  const educationLevels = [
    { value: 'any', label: 'Any Level' },
    { value: 'certificate', label: 'Certificate' },
    { value: 'bachelor', label: "Bachelor's Degree" },
    { value: 'master', label: "Master's Degree" },
    { value: 'phd', label: 'PhD' }
  ];

  return (
    <div style={{ padding: '20px' }}>
      <Title level={4} style={{ marginBottom: '20px', color: '#1890ff' }}>
        🎯 Screening Criteria Configuration
      </Title>
      
      <Alert
        message="Customize how candidates are evaluated"
        description="Set weightage for different factors and define minimum requirements for this job position."
        type="info"
        showIcon
        style={{ marginBottom: '24px' }}
      />

      {/* Weightage Configuration */}
      <Card 
        title={
          <Space>
            <span>⚖️ Evaluation Weights</span>
            <Tag color={totalWeight === 100 ? 'green' : 'red'}>
              Total: {totalWeight}%
            </Tag>
          </Space>
        }
        style={{ marginBottom: '24px' }}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Text strong>Skills & Technical Expertise</Text>
            <Slider
              min={0}
              max={70}
              value={weights.skillsWeight}
              onChange={(value) => handleWeightChange('skillsWeight', value)}
              marks={{ 0: '0%', 35: '35%', 70: '70%' }}
            />
            <Form.Item name="skillsWeight" hidden>
              <InputNumber />
            </Form.Item>
          </Col>
          
          <Col xs={24} md={12}>
            <Text strong>Experience Level</Text>
            <Slider
              min={0}
              max={50}
              value={weights.experienceWeight}
              onChange={(value) => handleWeightChange('experienceWeight', value)}
              marks={{ 0: '0%', 25: '25%', 50: '50%' }}
            />
            <Form.Item name="experienceWeight" hidden>
              <InputNumber />
            </Form.Item>
          </Col>
          
          <Col xs={24} md={12}>
            <Text strong>Education Background</Text>
            <Slider
              min={0}
              max={40}
              value={weights.educationWeight}
              onChange={(value) => handleWeightChange('educationWeight', value)}
              marks={{ 0: '0%', 20: '20%', 40: '40%' }}
            />
            <Form.Item name="educationWeight" hidden>
              <InputNumber />
            </Form.Item>
          </Col>
          
          <Col xs={24} md={12}>
            <Text strong>Location Match</Text>
            <Slider
              min={0}
              max={30}
              value={weights.locationWeight}
              onChange={(value) => handleWeightChange('locationWeight', value)}
              marks={{ 0: '0%', 15: '15%', 30: '30%' }}
            />
            <Form.Item name="locationWeight" hidden>
              <InputNumber />
            </Form.Item>
          </Col>
        </Row>
        
        {totalWeight !== 100 && (
          <Alert
            message={`Weights must total 100%. Current total: ${totalWeight}%`}
            type="warning"
            showIcon
            style={{ marginTop: '16px' }}
          />
        )}
      </Card>

      {/* Requirements Configuration */}
      <Card title="📋 Job Requirements" style={{ marginBottom: '24px' }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Form.Item
              name="requiredExperience"
              label={<Text strong>Minimum Years of Experience</Text>}
              initialValue={0}
            >
              <InputNumber
                min={0}
                max={20}
                style={{ width: '100%' }}
                placeholder="0"
                addonAfter="years"
              />
            </Form.Item>
          </Col>
          
          <Col xs={24} md={12}>
            <Form.Item
              name="requiredEducation"
              label={<Text strong>Minimum Education Level</Text>}
              initialValue="any"
            >
              <Select style={{ width: '100%' }}>
                {educationLevels.map(level => (
                  <Option key={level.value} value={level.value}>
                    {level.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
      </Card>

      {/* Skills Configuration */}
      <Card title="🛠️ Skills Requirements" style={{ marginBottom: '24px' }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Form.Item
              name="coreSkills"
              label={<Text strong>Core Skills (Must-Have)</Text>}
              initialValue={[]}
            >
              <Select
                mode="tags"
                style={{ width: '100%' }}
                placeholder="Select or type core skills"
                options={skillSuggestions.map(skill => ({ value: skill, label: skill }))}
              />
            </Form.Item>
          </Col>
          
          <Col xs={24} md={12}>
            <Form.Item
              name="preferredSkills"
              label={<Text strong>Preferred Skills (Nice-to-Have)</Text>}
              initialValue={[]}
            >
              <Select
                mode="tags"
                style={{ width: '100%' }}
                placeholder="Select or type preferred skills"
                options={skillSuggestions.map(skill => ({ value: skill, label: skill }))}
              />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      {/* Scoring Configuration */}
      <Card title="📊 Scoring Configuration">
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Form.Item
              name="minimumScore"
              label={<Text strong>Minimum Passing Score</Text>}
              initialValue={60}
            >
              <Slider
                min={30}
                max={90}
                marks={{ 30: '30%', 60: '60%', 90: '90%' }}
                tipFormatter={(value) => `${value}%`}
              />
            </Form.Item>
          </Col>
          
          <Col xs={24} md={12}>
            <Form.Item
              name="autoReject"
              label={<Text strong>Auto-Reject Below Minimum</Text>}
              valuePropName="checked"
              initialValue={false}
            >
              <Switch
                checkedChildren="Yes"
                unCheckedChildren="No"
              />
            </Form.Item>
          </Col>
        </Row>
        
        <Alert
          message="Scoring Guidelines"
          description={
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li><strong>80%+:</strong> Strong candidates - proceed with interview</li>
              <li><strong>60-79%:</strong> Good potential - consider phone screening</li>
              <li><strong>Below 60%:</strong> May need development or training</li>
            </ul>
          }
          type="info"
          showIcon
        />
      </Card>
    </div>
  );
};

export default ScreeningCriteriaForm;

